import isUndefined from 'lodash/isUndefined';
import React from 'react';

import { Currency } from 'chaika-enums';
import { PayerType, PlanData, PlanType, ConditionsAgreement, PlanStatus } from 'chaika-frontend-types/frontend-cms';

import type { EhrPlanDataWithAvailable } from '@plan/types';
import { LongList } from '@store/abstract/LongList';
import { CurrencyIcon } from '@store/types';
import { User } from '@store/models/User';

import { InputModifiers } from '@components/Input';
import { PlanConditionsLink } from '@components/PlanConditionsLink';
import { RadioGroupProps } from '@components/RadioGroup';
import { OptionType, SelectHighlightColor } from '@components/Select';
import { CommonTestAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';

export interface FilterParams {
    payerType?: PayerType;
    planType?: PlanType[];
    type?: PlanType[];
    statuses?: PlanStatus[];
    status?: PlanStatus[];
    name?: string;
}

export interface PlanOptionData {
    subscriptionId?: string;
    planId?: string;
}

export interface PlanLabelParams {
    plan: EhrPlanDataWithAvailable | PlanData;
    count?: number;
    selectedSubscriptionId?: string;
    selectedPlanId?: string;
    withConditions?: boolean;
    user?: User;
}

interface ToOptionsParams {
    plans: EhrPlanDataWithAvailable[];
    count: number;
    currency: Currency;
    selectedPlanId: string;
    emptyLabel: string;
    discount?: string;
    flatDiscount?: string;
    selectedSubscriptionId?: string;
    subscriptionAsId?: boolean;
    renderedServiceTotalCost?: number;
    onDiscountChange?(event: React.FocusEvent<HTMLInputElement>): void;
    onFlatDiscountChange?(event: React.FocusEvent<HTMLInputElement>): void;
    onDiscountTypeChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

interface DiscountRadioParams extends Pick<
    ToOptionsParams,
    'discount' | 'currency' | 'flatDiscount' | 'onDiscountChange' | 'onFlatDiscountChange'
> {
    onDiscountTypeChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

interface HighlightParams {
    highlightColor?: SelectHighlightColor;
    highlightText?: string;
}

export class PlanList extends LongList<PlanData, PlanData, FilterParams> {
    protected create(params: PlanData): PlanData {
        return params;
    }

    protected filterValue(
        value: PlanData,
        { payerType, statuses, status, planType, type, name }: FilterParams,
    ): boolean {
        return (
            this.filterFieldValueIncludesString(
                { ...value, name: value.name + (value.share ? ` (${value.share}%)` : '') },
                name,
                'name',
            ) &&
            this.filterFieldValueByArray(value, statuses, 'status') &&
            this.filterFieldValueByArray(value, status, 'status') &&
            this.filterFieldValueByArray(value, type, 'type') &&
            this.filterFieldValueByArray(value, planType, 'type') &&
            this.filterFieldValue(value, payerType, 'payerType')
        );
    }
}

function toOptions({ plans, emptyLabel, ...params }: ToOptionsParams): OptionType<string, PlanOptionData>[] {
    return [{ value: '', label: emptyLabel }, ...plans.flatMap(plan => toOption({ plan, ...params }))];
}

function toSelectOptions({ plans, emptyLabel, ...params }: ToOptionsParams): OptionType[] {
    return plans.flatMap(plan => toOption({ plan, ...params })).concat({ value: '', label: emptyLabel });
}

function getDiscoutRadioProps({
    discount,
    currency,
    flatDiscount,
    onDiscountChange,
    onFlatDiscountChange,
    onDiscountTypeChange,
}: DiscountRadioParams): RadioGroupProps {
    return {
        value: !!discount ? 'discount' : 'flatDiscount',
        name: 'discountType',
        onChange: onDiscountTypeChange,
        options: [
            {
                value: 'discount',
                label: translate('percents'),
                withTextField: true,
                textFieldProps: {
                    dataTest: CommonTestAttributes.Discount,
                    value: discount ?? '',
                    min: 1,
                    autoFocus: true,
                    max: 100,
                    mask: 'D[0][0][0] %',
                    formatChars: { 0: '[0]', '?': '[0]', D: /[-0-9]/ },
                    modifiers: [InputModifiers.WithoutBottomLine],
                    onBlur: onDiscountChange,
                },
            },
            {
                value: 'flatDiscount',
                label: translate(`in_${currency}`),
                withTextField: true,
                textFieldProps: {
                    dataTest: CommonTestAttributes.FlatDiscount,
                    autoFocus: true,
                    value: flatDiscount ?? '',
                    min: 1,
                    max: 999999,
                    mask: `D[0][0][0][0][0][0] ${CurrencyIcon[currency]}`,
                    formatChars: { 0: '[0]', '?': '[0]', D: /[-0-9]/ },
                    modifiers: [InputModifiers.WithoutBottomLine],
                    onBlur: onFlatDiscountChange,
                },
            },
        ],
    };
}

function toOption({
    plan,
    count,
    selectedPlanId,
    selectedSubscriptionId,
    subscriptionAsId,
    renderedServiceTotalCost,
}: Omit<ToOptionsParams, 'plans' | 'emptyLabel'> & { plan: EhrPlanDataWithAvailable }): OptionType<
    string,
    PlanOptionData
>[] {
    const { highlightColor, highlightText } = getHighlightParams(plan, renderedServiceTotalCost);

    const toItem = (user?: User): OptionType<string, any> => ({
        highlightColor,
        highlightText,
        value: subscriptionAsId ? plan.subscription.id : plan.id,
        type: plan.type,
        subscriptionId: plan.subscription.id,
        planId: plan.id,
        label: getLabel({ plan, count, selectedSubscriptionId, selectedPlanId, withConditions: false, user }) as string,
        tooltip: !!plan.conditions ? translate('openConditions') : undefined,
        tooltipLink: !!plan.conditions ? `/plan/${plan.id}/conditions` : undefined,
        isDisabled: plan.available ? isDisabled(plan, count, selectedPlanId) : true,
    });

    return [toItem()]; // users.length > 0 ? users.map(toItem) : [toItem()];
}

function getLabel({
    plan,
    count,
    selectedSubscriptionId,
    selectedPlanId,
    withConditions,
    user,
}: PlanLabelParams): React.ReactNode {
    let label =
        user?.fullName && !plan.name.includes(` (менеджер `)
            ? `${plan.name} (${translate('manager')} ${user?.fullName})`
            : plan.name;

    if (plan.payerType === PayerType.Legal) {
        label += ` ${plan.share}%`;
    } else if (plan.type === PlanType.Patient) {
        label += ` ${plan.discount}%`;
    }

    const usageLeft =
        isUndefined(count) ||
        (isUndefined(selectedPlanId) && isUndefined(selectedSubscriptionId)) ||
        !('usageLeft' in plan)
            ? undefined
            : getUsageLeft(plan, count, selectedPlanId, selectedSubscriptionId);

    label = isUndefined(usageLeft) ? label : `${label}  (${translate('left').toLowerCase()} ${usageLeft})`;

    return withConditions && plan.conditions
        ? React.createElement(PlanConditionsLink, {
              text: label,
              link: `/plan/${plan.id}/conditions`,
          })
        : label;
}

function isDisabled({ id, usageLeft }: EhrPlanDataWithAvailable, count: number, selectedPlanId: string): boolean {
    return isUndefined(usageLeft) || id === selectedPlanId ? false : usageLeft < count;
}

function getUsageLeft(
    { id, subscription, usageLeft }: EhrPlanDataWithAvailable,
    count: number,
    selectedPlanId?: string,
    selectedSubscriptionId?: string,
): number | undefined {
    return isUndefined(usageLeft)
        ? undefined
        : id === selectedPlanId || selectedSubscriptionId === subscription.id
          ? usageLeft - count
          : usageLeft;
}

function getHighlightParams(
    { agreement, subscription: { moneyLeft } }: EhrPlanDataWithAvailable,
    renderedServiceTotalCost?: number,
): HighlightParams {
    let highlightColor;
    const highlightText: string[] = [];

    switch (agreement) {
        case ConditionsAgreement.Required: {
            highlightColor = SelectHighlightColor.Orange03;
            highlightText.push(translate('approve'));
            break;
        }
        case ConditionsAgreement.Excluded: {
            highlightColor = SelectHighlightColor.Red02;
            highlightText.push(translate('notIncluded'));
            break;
        }
        case ConditionsAgreement.Without: {
            highlightColor = SelectHighlightColor.Green;
            highlightText.push(translate('included'));
            break;
        }
    }

    if (renderedServiceTotalCost && moneyLeft && renderedServiceTotalCost >= moneyLeft) {
        highlightColor = SelectHighlightColor.Red02;
        highlightText.push(translate('coverageEmountExceeded'));
    }

    return {
        highlightColor,
        highlightText: highlightText.join('. '),
    };
}

export {
    getDiscoutRadioProps,
    toOptions as toPlanOptions,
    toSelectOptions as toPlanSelectOptions,
    getLabel as getPlanLabel,
};
